import Component from 'navigation/component/Component';
import store from 'store';
import { bindEmitterMethod, bindMethod } from 'helpers/bind';
import { EASE_IN } from 'helpers/easings';
import mqStore from 'store/mqStore';
import anime from 'animejs';
import scroll from 'core/scroll';
import MenuFirstPanel from './MenuFirstPanel';
import MenuSecondPanel from './MenuSecondPanel';
import { durations, pauseMotions } from './motion';
export default class Menu extends Component {
    mobile = false;
    _opened = false;
    openedAt = 0;
    categories = [];
    motions = [];
    get opened() {
        return this._opened;
    }
    get panelChildren() {
        return [...this.modules.firstPanel.el.children, ...this.modules.secondPanel.el.children];
    }
    set opened(value) {
        this._opened = value;
        this.el.classList.toggle('visible', value);
        document.body.classList.toggle('menu-opened', value);
    }
    initialized() {
        super.initialized();
        this.categories = this.modules.secondPanel.modules.inners.map((inner) => inner.name);
    }
    bindEvents(add) {
        const emitterMethod = bindEmitterMethod(add);
        const method = bindMethod(add);
        store.drawer.toggleListener(add, this.onDrawerChange);
        this.modules.firstPanel[emitterMethod]('category:change', this.onCategoryUpdate);
        this.modules.secondPanel[emitterMethod]('category:change', this.onCategoryUpdate);
        this.modules.firstPanel[emitterMethod]('open:complete', this.onTriggerSecondPanel);
        this.modules.firstPanel[emitterMethod]('open:begin', this.onFirstPanelOpenBegin);
        this.modules.secondPanel[emitterMethod]('open:begin', this.onSecondPanelOpenBegin);
        mqStore.narrowDesktop.listenAndStart((mobile) => this.onMqChange(mobile));
        this.refs.overlay[method]('click', () => store.drawer.set(null));
        this.refs.overlay[method]('mouseenter', this.onOverlayEnter);
        store.panel.toggleListener(add, () => store.drawer.set(null));
    }
    getModulesMap() {
        return {
            firstPanel: ['.menu-level__first', MenuFirstPanel],
            secondPanel: ['.menu-level__second', MenuSecondPanel]
        };
    }
    onMqChange = (mobile) => {
        this.mobile = mobile;
        store.drawer.set(null);
    };
    onSecondPanelOpenBegin = () => {
        if (this.mobile)
            this.modules.firstPanel.leave();
    };
    onFirstPanelOpenBegin = () => {
        this.modules.secondPanel.forceClose();
        this.modules.secondPanel.reset();
    };
    onTriggerSecondPanel = () => {
        const category = store.category.get();
        const subcategory = store.subcategory.get();
        if (!this.categories.includes(category))
            return;
        if (subcategory && this.modules.secondPanel.list === null) {
            if (this.mobile)
                this.modules.firstPanel.open(true);
            this.modules.secondPanel.autoOpen = true;
            this.modules.secondPanel.list = store.category.get();
            this.modules.secondPanel.onLinkOut();
        }
    };
    onOverlayEnter = () => {
        // Ignore hover-close while the second panel is still sliding in:
        // the overlay is briefly exposed under the header during the animation
        if (Date.now() - this.openedAt < durations.SECOND_PANEL_TRANSLATE_IN)
            return;
        store.drawer.set(null);
    };
    onDrawerChange = (drawer, previous) => {
        const isOpen = drawer === 'menu';
        // Other drawers (search, filters…) must not cancel menu motions or unlock scroll
        if (!isOpen && previous !== 'menu')
            return;
        this.cancelMotions();
        const category = store.category.get();
        const subcategory = store.subcategory.get();
        if (isOpen) {
            // An interrupted close never reaches its complete callback: clean up now
            this.onCloseComplete();
            this.openedAt = Date.now();
            scroll.lock(true);
            if (category !== null && this.categories.includes(category)) {
                if (subcategory !== null) {
                    this.modules.firstPanel.category = category;
                    this.modules.firstPanel.onItemOut();
                }
                if (!this.mobile || subcategory === null)
                    this.modules.firstPanel.open();
                else
                    this.onTriggerSecondPanel();
            }
            else {
                this.modules.firstPanel.open();
            }
            this.opened = true;
        }
        else {
            scroll.unlock(true);
            if (this.opened) {
                this.opened = false;
                if (this.modules.secondPanel.list)
                    this.closeBothPanels();
                else
                    this.modules.firstPanel.close();
            }
        }
        this.refs.overlay.classList.toggle('visible', isOpen);
    };
    onCategoryUpdate = (category) => {
        if (this.mobile && category === null) {
            this.modules.firstPanel.enter();
            this.modules.firstPanel.category = null;
            this.modules.firstPanel.onItemOut();
        }
        this.modules.secondPanel.list = category;
    };
    closeBothPanels = () => {
        if (mqStore.narrowDesktop.get())
            this.closeBothPanelsMobile();
        else
            this.closeBothPanelsDesktop();
    };
    onCloseComplete = () => {
        this.el.style.transform = '';
        this.el.style.opacity = '';
        this.modules.firstPanel.reset();
        this.modules.secondPanel.reset();
        this.modules.firstPanel.opened = false;
        this.modules.secondPanel.list = null;
        this.modules.firstPanel.category = null;
        this.modules.firstPanel.onItemOut();
        this.modules.secondPanel.onListOut();
        this.modules.firstPanel.onListOut();
        this.modules.secondPanel.modules.inners.forEach((inner) => inner.reset());
        this.panelChildren.forEach((inner) => inner.removeAttribute('style'));
    };
    closeBothPanelsMobile = () => {
        this.cancelMotions();
        const inners = this.panelChildren;
        const m0 = anime({
            targets: [inners],
            opacity: {
                value: 0,
                duration: durations.FIRST_PANEL_OPACITY_OUT / 2,
                easing: 'linear'
            }
        });
        const m = anime({
            targets: [this.modules.firstPanel.el, this.modules.secondPanel.el],
            translateX: {
                value: [0, '-100%'],
                duration: durations.FIRST_PANEL_TRANSLATE_OUT,
                easing: 'easeInSine'
            },
            opacity: {
                value: [1, 0],
                duration: durations.FIRST_PANEL_OPACITY_OUT,
                delay: (durations.FIRST_PANEL_OPACITY_OUT) / 2,
                easing: 'linear'
            },
            complete: this.onCloseComplete
        });
        this.motions.push(m0, m);
    };
    closeBothPanelsDesktop = () => {
        this.cancelMotions();
        const inners = this.modules.secondPanel.modules.inners.map((inner) => inner.el);
        const m0 = anime({
            targets: [inners],
            opacity: {
                value: 0,
                delay: durations.SECOND_PANEL_OPACITY_OUT / 4,
                duration: durations.SECOND_PANEL_OPACITY_OUT / 2,
                easing: 'linear'
            }
        });
        const m = anime({
            targets: this.modules.secondPanel.el,
            translateY: {
                value: [0, '-100%'],
                duration: durations.SECOND_PANEL_TRANSLATE_OUT,
                easing: EASE_IN
            },
            opacity: {
                value: [1, 0],
                duration: durations.SECOND_PANEL_OPACITY_OUT,
                delay: durations.SECOND_PANEL_OPACITY_OUT / 2,
                easing: 'linear'
            },
            complete: this.onCloseComplete
        });
        this.motions.push(m0, m);
    };
    cancelMotions = () => {
        this.modules.firstPanel.cancelMotions();
        this.modules.secondPanel.cancelMotions();
        pauseMotions(this.motions);
        this.motions = [];
        this.panelChildren.forEach((inner) => inner.removeAttribute('style'));
    };
}
